/**
 * Timezone-aware datetime helpers.
 *
 * Source of truth in DB: UTC ISO strings.
 * Source of truth for display: the event's location timezone (IANA, e.g. "Asia/Yerevan").
 * Fallback when no event TZ: viewer's browser timezone.
 *
 * The "your time" hint is the viewer's browser-local time, shown only when it
 * differs from the event timezone at that moment.
 */

export const getBrowserTz = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/**
 * Returns true if two IANA zones produce the same wall-clock time at the given instant.
 * Used to decide whether to show a "your time" hint.
 */
export const tzsEqualAt = (date: Date, a?: string | null, b?: string | null): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    const fmt = (tz: string) =>
      new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(date);
    return fmt(a) === fmt(b);
  } catch {
    return false;
  }
};

/**
 * Returns the IANA tz → date parts at the given UTC instant.
 */
const partsInTz = (date: Date, tz: string) => {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value]),
  ) as Record<string, string>;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour === "24" ? "00" : parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

/**
 * Convert a UTC ISO string to a `YYYY-MM-DDTHH:mm` value usable by
 * `<input type="datetime-local">`, rendered in the given IANA timezone.
 *
 * @param iso UTC ISO string (or empty)
 * @param tz IANA tz name; falls back to browser tz when empty
 */
export function toDatetimeLocalInTz(
  iso: string,
  tz?: string | null,
  withSeconds = false,
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const zone = tz || getBrowserTz();
  const p = partsInTz(d, zone);
  const pad = (n: number) => String(n).padStart(2, "0");
  const base = `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
  return withSeconds ? `${base}:${pad(p.second ?? 0)}` : base;
}

/**
 * Inverse of `toDatetimeLocalInTz`: given a wall-clock `YYYY-MM-DDTHH:mm`
 * in the given IANA timezone, return the UTC ISO string.
 *
 * Implementation: probe with naive UTC, measure the zone offset at that instant,
 * subtract to recover the correct UTC. Iterate once to absorb DST edge cases.
 */
export function fromDatetimeLocalInTz(local: string, tz?: string | null): string {
  if (!local) return "";
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi, se = 0] = timePart.split(":").map(Number);
  if ([y, mo, d, h, mi, se].some((n) => isNaN(n))) return "";
  const zone = tz || getBrowserTz();

  // Start with a guess: treat the wall-clock as UTC.
  let guess = Date.UTC(y, mo - 1, d, h, mi, se);
  for (let i = 0; i < 2; i++) {
    const p = partsInTz(new Date(guess), zone);
    const got = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second ?? 0);
    const want = Date.UTC(y, mo - 1, d, h, mi, se);
    const diff = want - got;
    if (diff === 0) break;
    guess += diff;
  }
  return new Date(guess).toISOString();
}

/**
 * Format an ISO timestamp in a timezone with custom options.
 */
export function formatInTz(
  iso: string,
  tz: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale: string = "en-GB",
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: tz || getBrowserTz(),
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat(locale, options).format(d);
  }
}

/**
 * Short timezone abbreviation (e.g. "BST", "AMT", "GMT+4").
 */
export function tzShortName(iso: string, tz?: string | null): string {
  if (!tz) return "";
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(iso ? new Date(iso) : new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

/**
 * GMT offset string at a given instant (e.g. "GMT+4", "GMT-5").
 */
export function tzOffsetLabel(iso: string, tz?: string | null): string {
  const zone = tz || getBrowserTz();
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: zone,
      timeZoneName: "shortOffset",
    }).formatToParts(iso ? new Date(iso) : new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * Human-readable IANA tz label (e.g. "Asia/Yerevan" → "Yerevan").
 */
export function tzCityLabel(tz?: string | null): string {
  if (!tz) return "";
  const last = tz.split("/").pop();
  return (last ?? tz).replace(/_/g, " ");
}

/**
 * One-line date+time in the given zone: "Sat 9 May 2026, 14:00".
 */
export function formatEventDateTime(iso: string, tz?: string | null): string {
  return formatInTz(iso, tz, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Date-only in the given zone: "Sat 9 May 2026".
 */
export function formatEventDate(iso: string, tz?: string | null): string {
  return formatInTz(iso, tz, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Time-only in the given zone: "14:00".
 */
export function formatEventTime(iso: string, tz?: string | null): string {
  return formatInTz(iso, tz, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Time-only with seconds: "14:00:37". A gun time is a race start, so the
 * seconds are part of the reading, not noise to round away.
 */
export function formatGunTime(iso: string, tz?: string | null): string {
  return formatInTz(iso, tz, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * Compact date range honouring an event timezone:
 *  - Same day: "Sat 9 May 2026, 14:00 – 15:30"
 *  - Same month: "9 – 10 May 2026"
 *  - Same year: "9 May – 1 June 2026"
 *  - Different years: "30 Dec 2025 – 2 Jan 2026"
 */
export function formatEventDateRange(
  startIso: string,
  endIso: string | undefined,
  tz?: string | null,
): string {
  if (!startIso) return "";
  if (!endIso) return formatEventDate(startIso, tz);
  const zone = tz || getBrowserTz();
  const sParts = partsInTz(new Date(startIso), zone);
  const eParts = partsInTz(new Date(endIso), zone);
  const sameDay =
    sParts.year === eParts.year && sParts.month === eParts.month && sParts.day === eParts.day;
  const sameMonth = sParts.year === eParts.year && sParts.month === eParts.month;
  const sameYear = sParts.year === eParts.year;

  if (sameDay) {
    return `${formatEventDate(startIso, zone)}, ${formatEventTime(startIso, zone)} – ${formatEventTime(endIso, zone)}`;
  }
  if (sameMonth) {
    const month = formatInTz(startIso, zone, { month: "long" });
    return `${sParts.day} – ${eParts.day} ${month} ${sParts.year}`;
  }
  if (sameYear) {
    const sMonth = formatInTz(startIso, zone, { month: "long" });
    const eMonth = formatInTz(endIso, zone, { month: "long" });
    return `${sParts.day} ${sMonth} – ${eParts.day} ${eMonth} ${sParts.year}`;
  }
  return `${formatInTz(startIso, zone, { day: "numeric", month: "short", year: "numeric" })} – ${formatInTz(endIso, zone, { day: "numeric", month: "short", year: "numeric" })}`;
}
